import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import AdminLayout from "@/components/AdminLayout";
import api, { formatApiError } from "@/utils/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import DOMPurify from "dompurify";
import { Plus, Pencil, BookOpen, Users, Layers, Shield, ChevronRight, GraduationCap } from "lucide-react";
import RichTextEditor from "@/components/RichTextEditor";

export default function AdminCourses() {
  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);
  const [groups, setGroups] = useState([]);
  const [modules, setModules] = useState([]);
  const [students, setStudents] = useState([]);
  const [instructors, setInstructors] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: "", subtitle: "", description: "", instructor: "" });

  const load = useCallback(async () => {
    const [c, g, m, u, s] = await Promise.all([
      api.get("/courses"), api.get("/groups"), api.get("/modules"),
      api.get("/users", { params: { role: "instructor" } }),
      api.get("/students")
    ]);
    setCourses(c.data);
    setGroups(g.data);
    setModules(m.data);
    setInstructors(u.data);
    setStudents(s.data);
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = (e) => { e?.stopPropagation(); setEditing(null); setForm({ title: "", subtitle: "", description: "", instructor: "" }); setDialogOpen(true); };
  const openEdit = (e, c) => { e.stopPropagation(); setEditing(c); setForm({ title: c.title, subtitle: c.subtitle || "", description: c.description || "", instructor: c.instructor || "" }); setDialogOpen(true); };

  const handleSave = async () => {
    try {
      if (editing) { await api.put(`/courses/${editing.id}`, form); toast.success("Course updated"); }
      else { await api.post("/courses", form); toast.success("Course created"); }
      setDialogOpen(false); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const getGroupsForCourse = (cid) => groups.filter((g) => g.course_id === cid);
  const getModulesForCourse = (cid) => modules.filter((m) => m.course_id === cid);
  const getStudentsForCourse = (cid) => students.filter((s) => s.course_id === cid);

  return (
    <AdminLayout>
      <div data-testid="admin-courses" className="space-y-6 animate-fade-in-up">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-heading text-3xl sm:text-4xl text-[#5C3A2E] font-semibold tracking-tight">Courses</h1>
            <p className="text-[#2B2B2B]/50 mt-1 text-sm">Click a course to open its dashboard</p>
          </div>
          <Button data-testid="create-course-button" onClick={openCreate} className="bg-[#5C3A2E] text-white rounded-full hover:bg-[#5C3A2E]/90">
            <Plus className="w-4 h-4 mr-1.5" /> New Course
          </Button>
        </div>

        <div className="grid gap-5">
          {courses.map((c) => {
            const courseGroups = getGroupsForCourse(c.id);
            const courseModules = getModulesForCourse(c.id);
            const courseStudents = getStudentsForCourse(c.id);
            return (
              <div
                key={c.id}
                data-testid={`course-card-${c.id}`}
                onClick={() => navigate(`/admin/courses/${c.id}`)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === "Enter" && navigate(`/admin/courses/${c.id}`)}
                className="w-full text-left bg-white rounded-xl shadow-[0_8px_30px_rgb(92,58,46,0.08)] border border-[#EADBC8]/30 p-6 sm:p-8 studio-card group cursor-pointer"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <BookOpen className="w-5 h-5 text-[#C6A969]" />
                      <h2 className="font-heading text-xl sm:text-2xl text-[#5C3A2E] font-semibold">{c.title}</h2>
                      <ChevronRight className="w-4 h-4 text-[#2B2B2B]/20 group-hover:text-[#C6A969] transition-colors ml-auto flex-shrink-0" />
                    </div>
                    {c.subtitle && <p className="text-[#2B2B2B]/50 text-sm italic mb-2">{c.subtitle}</p>}
                    {c.description && <div className="text-[#2B2B2B]/60 text-sm line-clamp-2 mb-3 prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(c.description) }} />}
                    <div className="flex flex-wrap gap-4 text-xs text-[#2B2B2B]/50">
                      <div className="flex items-center gap-1.5"><Users className="w-3.5 h-3.5" /><span>{courseGroups.length} group{courseGroups.length !== 1 ? "s" : ""}</span></div>
                      <div className="flex items-center gap-1.5"><Layers className="w-3.5 h-3.5" /><span>{courseModules.length} module{courseModules.length !== 1 ? "s" : ""}</span></div>
                      <div className="flex items-center gap-1.5"><GraduationCap className="w-3.5 h-3.5" /><span>{courseStudents.length} student{courseStudents.length !== 1 ? "s" : ""}</span></div>
                    </div>
                  </div>
                  <Button data-testid={`edit-course-${c.id}`} variant="ghost" size="sm" onClick={(e) => openEdit(e, c)} className="text-[#5C3A2E] hover:bg-[#EADBC8]/30 rounded-full flex-shrink-0">
                    <Pencil className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            );
          })}
          {courses.length === 0 && (
            <div className="text-center py-12 text-[#2B2B2B]/40 text-sm">No courses yet. Create your first course.</div>
          )}
        </div>

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="bg-white rounded-xl max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl text-[#5C3A2E]">{editing ? "Edit Course" : "New Course"}</DialogTitle>
              <DialogDescription className="text-[#2B2B2B]/50 text-sm">{editing ? "Update course details" : "Create a new studio course"}</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 mt-2">
              <div>
                <label className="text-sm font-medium text-[#2B2B2B] mb-1 block">Title</label>
                <Input data-testid="course-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="rounded-xl border-[#EADBC8]" />
              </div>
              <div>
                <label className="text-sm font-medium text-[#2B2B2B] mb-1 block">Subtitle</label>
                <Input data-testid="course-subtitle-input" value={form.subtitle} onChange={(e) => setForm({ ...form, subtitle: e.target.value })} className="rounded-xl border-[#EADBC8]" />
              </div>
              <div>
                <label className="text-sm font-medium text-[#2B2B2B] mb-1 block">Instructor</label>
                <Input data-testid="course-instructor-input" value={form.instructor} onChange={(e) => setForm({ ...form, instructor: e.target.value })} className="rounded-xl border-[#EADBC8]" />
              </div>
              <div>
                <label className="text-sm font-medium text-[#2B2B2B] mb-1 block">Description</label>
                <RichTextEditor value={form.description} onChange={(val) => setForm({ ...form, description: val })} placeholder="Course description..." />
              </div>
              <Button data-testid="save-course-button" onClick={handleSave} className="w-full bg-[#5C3A2E] text-white rounded-full hover:bg-[#5C3A2E]/90 h-11">
                {editing ? "Save Changes" : "Create Course"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
